import type { Chip } from "@/shared/components/FilterChips";

import type { OrderFilters } from "../api/use-orders";

import { MARKETPLACE_LABEL } from "./marketplace-labels";
import { MARKETPLACE_STATUS_LABEL } from "./marketplace-status-labels";
import { joinTokens, searchTokens, type FilterUpdate } from "./order-url-state";

type T = (key: string, opts?: Record<string, unknown>) => string;
interface ChipContext {
  sellers: { shop_id: string; shop_name?: string }[];
  stageLabel: (code: string) => string;
}

/** One chip per active value, in bar order then drawer order. Pure: the page passes `onChange`. */
export function buildChips(
  f: OrderFilters,
  onChange: (u: FilterUpdate) => void,
  t: T,
  ctx: ChipContext,
): Chip[] {
  const chips: Chip[] = [];
  const tokens = searchTokens(f.search ?? "");
  for (const tok of tokens) {
    chips.push({
      key: `search:${tok}`,
      label: tok,
      onDismiss: () =>
        onChange({ search: joinTokens(tokens.filter((x) => x !== tok)) || undefined, offset: 0 }),
    });
  }
  if (f.marketplace)
    chips.push({
      key: "mkp",
      label: MARKETPLACE_LABEL[f.marketplace as keyof typeof MARKETPLACE_LABEL] ?? f.marketplace,
      onDismiss: () =>
        onChange({ marketplace: undefined, marketplace_status: undefined, offset: 0 }),
    });
  if (f.date_from || f.date_to)
    chips.push({
      key: "date",
      label: `${f.date_from ?? "…"} – ${f.date_to ?? "…"}`,
      onDismiss: () => onChange({ date_from: undefined, date_to: undefined, offset: 0 }),
    });
  if (f.exception_only)
    chips.push({
      key: "exc",
      label: t("exc_filter"),
      onDismiss: () => onChange({ exception_only: undefined, offset: 0 }),
    });
  if (f.status?.length)
    chips.push({
      key: "stage",
      label: t("chip_stage", { v: f.status.map(ctx.stageLabel).join(", ") }),
      onDismiss: () => onChange({ status: undefined, offset: 0 }),
    });
  if (f.awb)
    chips.push({
      key: "awb",
      label: t("chip_label", { v: t(`awb_${f.awb}`) }),
      onDismiss: () => onChange({ awb: undefined, offset: 0 }),
    });
  if (f.sof)
    chips.push({
      key: "sof",
      label: t("chip_sof", { v: t(f.sof === "true" ? "sof_only" : "sof_excluded") }),
      onDismiss: () => onChange({ sof: undefined, offset: 0 }),
    });
  if (f.courier)
    chips.push({
      key: "courier",
      label: t("chip_courier", { v: f.courier }),
      onDismiss: () => onChange({ courier: undefined, offset: 0 }),
    });
  if (f.ship_by)
    chips.push({
      key: "ship_by",
      label: t("chip_ship_by", { v: t(`ship_by_${f.ship_by}`) }),
      onDismiss: () => onChange({ ship_by: undefined, offset: 0 }),
    });
  if (f.marketplace_status)
    chips.push({
      key: "mkps",
      label: MARKETPLACE_STATUS_LABEL[f.marketplace_status] ?? f.marketplace_status,
      onDismiss: () => onChange({ marketplace_status: undefined, offset: 0 }),
    });
  if (f.gift)
    chips.push({
      key: "gift",
      label: t(f.gift === "true" ? "chip_gift_only" : "chip_gift_none"),
      onDismiss: () => onChange({ gift: undefined, offset: 0 }),
    });
  const channels = (f.payment_channel ?? "").split(",").filter(Boolean);
  for (const ch of channels) {
    chips.push({
      key: `pay:${ch}`,
      label: ch,
      onDismiss: () =>
        onChange({
          payment_channel: channels.filter((x) => x !== ch).join(",") || undefined,
          offset: 0,
        }),
    });
  }
  if (f.payment)
    chips.push({
      key: "pay",
      label: t(f.payment === "cod" ? "f_cod" : "f_prepaid"),
      onDismiss: () => onChange({ payment: undefined, offset: 0 }),
    });
  if (f.seller_id) {
    const s = ctx.sellers.find((x) => x.shop_id === f.seller_id);
    chips.push({
      key: "seller",
      label: s?.shop_name || f.seller_id,
      onDismiss: () => onChange({ seller_id: undefined, offset: 0 }),
    });
  }
  return chips;
}
